import axios from "axios";

let accessToken: string | null = null;
let authChangeListeners: Array<() => void> = [];

export function setAccessToken(token: string | null) {
  accessToken = token;
  authChangeListeners.forEach((fn) => fn());
}

export function getAccessToken() {
  return accessToken;
}

export function onAuthChange(fn: () => void) {
  authChangeListeners.push(fn);
  return () => {
    authChangeListeners = authChangeListeners.filter((l) => l !== fn);
  };
}

const client = axios.create({
  baseURL: "/api/v1",
  headers: { "Content-Type": "application/json" },
});

client.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (error.response?.data?.error?.code === "ACCOUNT_INACTIVE") {
        setAccessToken(null);
        localStorage.removeItem("refresh_token");
        localStorage.setItem("auth_error", "account_inactive");
        return Promise.reject(error);
      }
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken) {
        try {
          const { data } = await axios.post("/api/v1/auth/refresh", {
            refresh: refreshToken,
          });
          setAccessToken(data.access);
          localStorage.setItem("refresh_token", data.refresh);
          originalRequest.headers.Authorization = `Bearer ${data.access}`;
          return client(originalRequest);
        } catch (refreshError) {
          const refreshResp = (refreshError as { response?: { data?: { error?: { code?: string } } } })?.response;
          if (refreshResp?.data?.error?.code === "ACCOUNT_INACTIVE") {
            localStorage.setItem("auth_error", "account_inactive");
          }
          setAccessToken(null);
          localStorage.removeItem("refresh_token");
          // Don't redirect — let React Router handle it via auth state
        }
      }
    }
    return Promise.reject(error);
  }
);

export default client;
